import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteEditBasic } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, get, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
  title: string
  originalTitle: string
}

export default function updateClassWebsiteTitleAction(payload: Payload): PreparedAction {
  return {
    name: "UPDATE_CLASS_WEBSITE_TITLE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).set(
    "attributes.title",
    payload.title
  ).patch

  return [patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).set(
    "attributes.title",
    payload.originalTitle
  ).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let newTitle = payload.title
  let originalTitle = payload.originalTitle
  let websiteSlug = get(models, "classWebsite.attributes.slug")
  let context = assign({}, defaultClassWebsiteContext(payload, models), {
    newTitle,
    originalTitle,
    websiteSlug,
  })
  let newEvent: ClassWebsiteEditBasic = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: "title",
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Renamed Class Website",
    titleForAnalytics: "Class Website - Edit Title",
    description: `Change your website's name from "${originalTitle}" to "${originalTitle}"`,
    activity: defaultClassWebsiteActivity(payload, models),
    context,
    newAnalyticsEvents: [newEvent],
  }
}
